import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './MCConnections.css';
import logo from '../../images/audithippo.png';

const API_URL        = '/rest/api/v1/admin/connections';
const ADD_API_URL    = '/rest/api/v1/admin/connections/add';
const UPDATE_API_URL = '/rest/api/v1/admin/connections/update';
const REMOVE_API_URL          = '/rest/api/v1/admin/connections/remove';
const TOGGLE_STATUS_API_URL   = '/rest/api/v1/admin/connections/deactivate';

const EMPTY_FORM = {
    name: '',
    subdomain: '',
    mid: '',
    environment: 'Production',
    clientId: '',
    clientSecret: '',
};

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const now = new Date();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (d.toDateString() === now.toDateString()) return `Today, ${time}`;
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + time;
}

function deriveStatus(item) {
    if (typeof item.isConnected === 'boolean') return item.isConnected ? 'Connected' : 'Disconnected';
    const raw = (item.status || item.connectionStatus || item.state || '').toString().toLowerCase();
    if (!raw) return 'Connected';
    if (['connected', 'active', 'ok', 'healthy', 'success'].includes(raw)) return 'Connected';
    return 'Disconnected';
}

function normalizeConnection(item, index) {
    const env       = item.environment || item.env || 'Production';
    const envClass  = env.toLowerCase().includes('sandbox') ? 'sandbox' : 'prod';
    const subdomain = item.subdomain || '';
    const status    = deriveStatus(item);
    return {
        id:           item.id || item.connectionId || index + 1,
        name:         item.name || item.instanceName || `${item.orgName || 'Instance'} — ${env}`,
        env,
        envClass,
        status,
        statusClass:  status === 'Connected' ? 'connected' : 'disconnected',
        mid:          String(item.mid || item.accountId || ''),
        lastSynced:   formatDate(item.lastSyncedAt || item.lastSynced || item.updatedAt),
        subdomain,
        clientId:     item.clientId || '',
        clientSecret: item.clientSecret || '••••••••••••••••••',
        authUrl:      item.authUrl || (subdomain ? `https://${subdomain}.auth.marketingcloudapis.com/` : ''),
        restUrl:      item.restUrl || item.restBaseUrl || (subdomain ? `https://${subdomain}.rest.marketingcloudapis.com/` : ''),
        soapUrl:      item.soapUrl || item.soapBaseUrl || (subdomain ? `https://${subdomain}.soap.marketingcloudapis.com/` : ''),
        scopes:       item.scopes || item.permissions || [],
        users:        Array.isArray(item.users) ? item.users : [],
    };
}

function MCConnections() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [expandedCards, setExpandedCards] = useState({});
    const [secretVisible, setSecretVisible] = useState({});
    const [offcanvasOpen, setOffcanvasOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);
    const [form, setForm]               = useState(EMPTY_FORM);
    const [submitting, setSubmitting]   = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [showValidation, setShowValidation] = useState(false);
    const [editOpen, setEditOpen]               = useState(false);
    const [editForm, setEditForm]               = useState({ connectionId: '', name: '', subdomain: '', mid: '', environment: 'Production', clientId: '', clientSecret: '', status: 'Connected' });
    const [editSubmitting, setEditSubmitting]   = useState(false);
    const [editError, setEditError]             = useState(null);
    const [removingId, setRemovingId]           = useState(null);
    const [togglingId, setTogglingId]           = useState(null);

    const fetchConnections = () => {
        return axios.get(API_URL)
            .then(res => {
                const data = res.data;
                const list = Array.isArray(data) ? data : (data.items || data.connections || data.data || []);
                setConnections(list.map(normalizeConnection));
            })
            .catch(err => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchConnections(); }, []);

    const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const submitConnection = () => {
        setSubmitting(true);
        setSubmitError(null);
        return axios.post(ADD_API_URL, {
            name:         form.name,
            subdomain:    form.subdomain,
            mid:          form.mid,
            environment:  form.environment,
            clientId:     form.clientId,
            clientSecret: form.clientSecret,
        })
            .then(() => {
                setStep(3);
                fetchConnections();
            })
            .catch(err => setSubmitError(err.response?.data?.message || err.message))
            .finally(() => setSubmitting(false));
    };

    const toggleCard = (id) => {
        setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleSecret = (id) => {
        setSecretVisible(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const openOffcanvas = () => {
        setStep(1);
        setForm(EMPTY_FORM);
        setSubmitError(null);
        setShowValidation(false);
        setOffcanvasOpen(true);
    };
    const closeOffcanvas = () => {
        setOffcanvasOpen(false);
        setStep(1);
        setForm(EMPTY_FORM);
        setSubmitError(null);
        setShowValidation(false);
    };

    const openEdit = (conn) => {
        setEditForm({
            connectionId: conn.id || '',
            name:         conn.name || '',
            subdomain:    conn.subdomain || '',
            mid:          conn.mid || '',
            environment:  conn.env || 'Production',
            clientId:     conn.clientId || '',
            clientSecret: '',
            status:       conn.status || 'Connected',
        });
        setEditError(null);
        setEditOpen(true);
    };
    const closeEdit = () => {
        if (editSubmitting) return;
        setEditOpen(false);
        setEditError(null);
    };
    const updateEdit = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

    const submitEdit = () => {
        if (editSubmitting) return;
        setEditSubmitting(true);
        setEditError(null);
        const payload = {
            id:       editForm.connectionId,
            name:     editForm.name,
            clientId: editForm.clientId,
            status:   editForm.status,
        };
        if (editForm.clientSecret.trim() !== '') payload.clientSecret = editForm.clientSecret;
        axios.post(UPDATE_API_URL, payload)
            .then(() => {
                setEditOpen(false);
                fetchConnections();
            })
            .catch(err => setEditError(err.response?.data?.message || err.message))
            .finally(() => setEditSubmitting(false));
    };

    const editValid = editForm.name.trim() !== '' && editForm.clientId.trim() !== '';

    const handleRemove = (conn) => {
        if (removingId) return;
        const ok = window.confirm(`Remove ${conn.name}? This cannot be undone.`);
        if (!ok) return;
        setRemovingId(conn.id);
        axios.post(REMOVE_API_URL, { id: conn.id })
            .then(() => fetchConnections())
            .catch(err => {
                console.error('[MCConnections] failed to remove:', err.response?.status, err.response?.data, err.message);
                window.alert(`Failed to remove connection: ${err.response?.data?.message || err.message}`);
            })
            .finally(() => setRemovingId(null));
    };

    const handleToggleStatus = (conn) => {
        if (togglingId) return;
        const isConnected = conn.status === 'Connected';
        const action      = isConnected ? 'Deactivate' : 'Activate';
        const ok = window.confirm(`${action} ${conn.name}?`);
        if (!ok) return;
        setTogglingId(conn.id);
        axios.post(TOGGLE_STATUS_API_URL, { id: conn.id })
            .then(() => fetchConnections())
            .catch(err => {
                console.error('[MCConnections] failed to toggle status:', err.response?.status, err.response?.data, err.message);
                window.alert(`Failed to ${action.toLowerCase()} connection: ${err.response?.data?.message || err.message}`);
            })
            .finally(() => setTogglingId(null));
    };

    const handleBack = () => {
        if (submitting) return;
        if (step === 1) closeOffcanvas();
        else setStep(s => s - 1);
    };

    const handleNext = () => {
        if (submitting) return;
        if (step === 3) { closeOffcanvas(); return; }
        if (step === 2) {
            if (!step2Valid) { setShowValidation(true); return; }
            submitConnection();
            return;
        }
        if (step === 1 && !step1Valid) { setShowValidation(true); return; }
        setShowValidation(false);
        setStep(s => s + 1);
    };

    const step1Valid = form.name.trim() !== '' && form.subdomain.trim() !== '' && form.mid.trim() !== '' && !!form.environment;
    const step2Valid = form.clientId.trim() !== '' && form.clientSecret.trim() !== '';
    const stepValid  = step === 1 ? step1Valid : (step === 2 ? step2Valid : true);

    return (
        <>
            {/* Mobile Menu Overlay */}
            <div className={`mobile-menu-overlay${menuOpen ? ' show' : ''}`} onClick={() => setMenuOpen(false)} />

            {/* Mobile Slide Menu */}
            <div className={`mobile-menu${menuOpen ? ' show' : ''}`}>
                <div className="mobile-menu-header">
                    <a href="#" className="logobock d-flex align-items-center gap-2">
                        <div className="hippolog"><img src={logo} alt="AuditHippo" /></div>
                        <span className="brand-name">Audit<span className="hippotxt">Hippo</span></span>
                    </a>
                    <div className="mobile-menu-close" onClick={() => setMenuOpen(false)} role="button" aria-label="Close menu">&#10005;</div>
                </div>
                <div className="mobile-menu-items">
                    <div className="mobile-menu-item" onClick={() => navigate('/journeys')}>Journeys</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/data-extensions')}>Data Extensions</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/automations')}>Automations</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/email-studio')}>Email Studio</div>
                    <div className="mobile-menu-item" onClick={() => navigate('/cloud-pages')}>Cloud Pages</div>
                    <div className="mobile-menu-item active">Admin</div>
                </div>
            </div>

            {/* Navbar */}
            <nav className="navbar-custom">
                <div className="adm-navbar-container">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <button className="mobile-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                                <span /><span /><span />
                            </button>
                            <div className="d-flex align-items-center gap-2">
                                <a href="#" className="logobock d-flex align-items-center gap-2">
                                    <div className="hippolog"><img src={logo} alt="AuditHippo" /></div>
                                    <span className="brand-name">Audit<span className="hippotxt">Hippo</span></span>
                                </a>
                                <span className="divider-mobile" />
                                <span className="marketing-cloud-text">Admin</span>
                            </div>
                        </div>
                        <button
                            className="environment-badge d-flex align-items-center gap-2"
                            onClick={() => { localStorage.removeItem('isAdminLoggedIn'); localStorage.removeItem('adminAuthToken'); localStorage.removeItem('adminSessionId'); navigate('/admin/login'); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                            <div className="environment-dot" />
                            <span className="environment-text">Logout</span>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Page */}
            <div className="adm-page">
                <div className="adm-admin-layout">
                    {/* Sidebar */}
                    <aside className="adm-sidebar">
                        <div className="adm-sidebar-box">
                            <div className="adm-sidebar-header">
                                <p className="adm-sidebar-title">Admin Settings</p>
                            </div>
                            <div className="adm-sidebar-tabs">
                                <button className="adm-sidebar-tab active" type="button">
                                    <span>&#9729;</span> MC Connections
                                </button>
                                <button className="adm-sidebar-tab" type="button" onClick={() => navigate('/admin/user-management')}>
                                    <span>&#128101;</span> User Management
                                </button>
                                <button className="adm-sidebar-tab" type="button" onClick={() => navigate('/admin/billing')}>
                                    <span>&#128179;</span> Billing &amp; Plan
                                </button>
                            </div>
                            <div className="adm-sidebar-back">
                                <button className="adm-btn-back" type="button" onClick={() => navigate('/automations')}>
                                    &larr; Back to AuditHippo
                                </button>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="adm-main">
                        <div className="adm-section-header">
                            <div>
                                <h2 className="adm-section-title">Marketing Cloud Connections</h2>
                                <p className="adm-section-subtitle">Manage API credentials and connected instances</p>
                            </div>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={openOffcanvas}>
                                <span className="me-1">+</span> Connect Instance
                            </button>
                        </div>

                        <div className="adm-info-banner">
                            <span className="adm-info-icon">&#8505;</span>
                            <p className="adm-info-text">
                                AuditHippo uses <strong>Server-to-Server OAuth 2.0</strong> to connect to Marketing Cloud.
                                Create an installed package in your MC instance with the required scopes,
                                then paste the credentials below. Secrets are encrypted at rest using AES-256.
                            </p>
                        </div>

                        <div className="d-flex flex-column gap-3">
                            {loading && <p style={{ textAlign: 'center', padding: 24, color: '#94A3B8' }}>Loading connections…</p>}
                            {error && <p style={{ textAlign: 'center', padding: 24, color: '#EF4444' }}>Failed to load: {error}</p>}

                            {!loading && !error && connections.map(conn => (
                                <ConnectionCard
                                    key={conn.id}
                                    conn={conn}
                                    expanded={!!expandedCards[conn.id]}
                                    onToggle={() => toggleCard(conn.id)}
                                    secretVisible={!!secretVisible[conn.id]}
                                    onToggleSecret={() => toggleSecret(conn.id)}
                                    onEdit={() => openEdit(conn)}
                                    onRemove={() => handleRemove(conn)}
                                    removing={removingId === conn.id}
                                    onToggleStatus={() => handleToggleStatus(conn)}
                                    toggling={togglingId === conn.id}
                                />
                            ))}

                            {!loading && !error && (
                                <button type="button" className="adm-connect-empty" onClick={openOffcanvas}>
                                    <span style={{ fontSize: '1.4rem' }}>+</span>
                                    Connect another instance
                                </button>
                            )}
                        </div>
                    </main>
                </div>
            </div>

            {/* Offcanvas: Connect Instance */}
            {offcanvasOpen && (
                <>
                    <div className="adm-offcanvas-overlay" onClick={closeOffcanvas} />
                    <div className="adm-offcanvas">
                        <div className="adm-offcanvas-header">
                            <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                                <div>
                                    <h2 className="modal-title-text">Connect Marketing Cloud</h2>
                                    <p className="panel-subtitle">Add a new Marketing Cloud instance to AuditHippo</p>
                                </div>
                                <button type="button" className="btn-close-custom" onClick={closeOffcanvas} aria-label="Close">&times;</button>
                            </div>
                        </div>

                        <div className="adm-offcanvas-body">
                            {/* Stepper */}
                            <div className="adm-stepper-bar">
                                {[
                                    { n: 1, label: 'Instance Details' },
                                    { n: 2, label: 'API Credentials' },
                                    { n: 3, label: 'Test & Connect' },
                                ].map((s, i) => (
                                    <div key={s.n} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        <div className={`adm-step-item${step === s.n ? ' active' : ''}${step > s.n ? ' complete' : ''}`}>
                                            <div className="adm-step-circle">{step > s.n ? '✓' : s.n}</div>
                                            <span className="adm-step-label">{s.label}</span>
                                        </div>
                                        {i < 2 && <div className="adm-stepper-sep" />}
                                    </div>
                                ))}
                            </div>

                            {/* Step 1 */}
                            {step === 1 && (
                                <div className="d-flex flex-column gap-3">
                                    <div>
                                        <label className="adm-input-label">Instance Name</label>
                                        <input className="adm-text-input" type="text" placeholder="e.g. ACME Corp — Production"
                                            style={showValidation && !form.name.trim() ? { borderColor: '#EF4444' } : undefined}
                                            value={form.name} onChange={e => updateForm('name', e.target.value)} />
                                        {showValidation && !form.name.trim()
                                            ? <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Instance Name is required.</p>
                                            : <p className="adm-input-hint mt-1">A friendly name to identify this connection in AuditHippo.</p>}
                                    </div>
                                    <div>
                                        <label className="adm-input-label">Subdomain</label>
                                        <input className="adm-text-input" type="text" placeholder="e.g. acme"
                                            style={showValidation && !form.subdomain.trim() ? { borderColor: '#EF4444' } : undefined}
                                            value={form.subdomain} onChange={e => updateForm('subdomain', e.target.value)} />
                                        {showValidation && !form.subdomain.trim()
                                            ? <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Subdomain is required.</p>
                                            : <p className="adm-input-hint mt-1">Your Marketing Cloud subdomain (found in your MC URL).</p>}
                                    </div>
                                    <div>
                                        <label className="adm-input-label">Account MID</label>
                                        <input className="adm-text-input" type="text" placeholder="e.g. 514003821"
                                            style={showValidation && !form.mid.trim() ? { borderColor: '#EF4444' } : undefined}
                                            value={form.mid} onChange={e => updateForm('mid', e.target.value)} />
                                        {showValidation && !form.mid.trim()
                                            ? <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Account MID is required.</p>
                                            : <p className="adm-input-hint mt-1">Your Marketing Cloud Account ID (MID).</p>}
                                    </div>
                                    <div>
                                        <label className="adm-input-label mb-2">Environment</label>
                                        <div className="row g-2">
                                            <div className="col-6">
                                                <label className="adm-env-choice adm-text-input d-flex align-items-center gap-2 mb-0">
                                                    <input type="radio" name="admEnv" value="Production"
                                                        checked={form.environment === 'Production'}
                                                        onChange={() => updateForm('environment', 'Production')} /> Production
                                                </label>
                                            </div>
                                            <div className="col-6">
                                                <label className="adm-env-choice adm-text-input d-flex align-items-center gap-2 mb-0">
                                                    <input type="radio" name="admEnv" value="Sandbox"
                                                        checked={form.environment === 'Sandbox'}
                                                        onChange={() => updateForm('environment', 'Sandbox')} /> Sandbox
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 2 */}
                            {step === 2 && (
                                <div className="d-flex flex-column gap-3">
                                    <div className="adm-warning-box">
                                        <p className="adm-warning-text">
                                            Create a <strong>Server-to-Server</strong> installed package in Marketing Cloud with the required API scopes. Copy the Client ID and Secret below.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="adm-input-label">Client ID</label>
                                        <input className="adm-text-input" type="text" placeholder="Paste your Client ID"
                                            style={showValidation && !form.clientId.trim() ? { borderColor: '#EF4444' } : undefined}
                                            value={form.clientId} onChange={e => updateForm('clientId', e.target.value)} />
                                        {showValidation && !form.clientId.trim() && (
                                            <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Client ID is required.</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="adm-input-label">Client Secret</label>
                                        <div className="adm-secret-field">
                                            <input className="adm-text-input" type="password" placeholder="Paste your Client Secret"
                                                style={showValidation && !form.clientSecret.trim() ? { borderColor: '#EF4444' } : undefined}
                                                value={form.clientSecret} onChange={e => updateForm('clientSecret', e.target.value)} />
                                        </div>
                                        {showValidation && !form.clientSecret.trim() && (
                                            <p className="mt-1" style={{ color: '#EF4444', fontSize: 12, margin: 0 }}>Client Secret is required.</p>
                                        )}
                                    </div>
                                    <div>
                                        <p className="adm-required-title">Required Scopes</p>
                                        <div className="d-flex flex-wrap gap-2">
                                            {['Email Read/Write', 'Automations Read/Write', 'Data Extensions Read/Write', 'Journey Builder Read/Write', 'Cloud Pages Read', 'List and Subscribers'].map(s => (
                                                <span key={s} className="adm-required-pill">{s}</span>
                                            ))}
                                        </div>
                                    </div>
                                    {submitError && (
                                        <p style={{ color: '#EF4444', margin: 0 }}>Failed to add connection: {submitError}</p>
                                    )}
                                </div>
                            )}

                            {/* Step 3 */}
                            {step === 3 && (
                                <div className="d-flex flex-column gap-3">
                                    <div className="adm-success-box">
                                        <div className="adm-success-check">&#10003;</div>
                                        <p className="adm-success-title">Connection Successful</p>
                                        <p className="adm-success-sub">AuditHippo can reach your Marketing Cloud instance.</p>
                                    </div>
                                    <div className="d-flex flex-column">
                                        {['Auth Endpoint', 'REST API', 'SOAP API', 'Data Extensions Read', 'Automations Read'].map(ep => (
                                            <div key={ep} className="adm-endpoint-row">
                                                <span className="adm-endpoint-label">{ep}</span>
                                                <span className="adm-endpoint-ok">&#10003; OK</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="adm-offcanvas-footer">
                            <button type="button" className="btn-admin btn-admin-secondary btn-admin-md" onClick={handleBack} disabled={submitting}>
                                {step === 1 ? 'Cancel' : 'Back'}
                            </button>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={handleNext} disabled={submitting}>
                                {step === 3 ? 'Finish' : (step === 2 ? (submitting ? 'Connecting…' : 'Connect →') : 'Continue →')}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Offcanvas: Edit Connection */}
            {editOpen && (
                <>
                    <div className="adm-offcanvas-overlay" onClick={closeEdit} />
                    <div className="adm-offcanvas">
                        <div className="adm-offcanvas-header">
                            <div className="d-flex align-items-start justify-content-between w-100 mb-3">
                                <div>
                                    <h2 className="modal-title-text">Edit Marketing Cloud Connection</h2>
                                    <p className="panel-subtitle">Update credentials and details for this instance</p>
                                </div>
                                <button type="button" className="btn-close-custom" onClick={closeEdit} aria-label="Close">&times;</button>
                            </div>
                        </div>

                        <div className="adm-offcanvas-body">
                            <div className="d-flex flex-column gap-3">
                                <div>
                                    <label className="adm-input-label">Instance Name</label>
                                    <input className="adm-text-input" type="text"
                                        value={editForm.name} onChange={e => updateEdit('name', e.target.value)} />
                                </div>
                                <div>
                                    <label className="adm-input-label">Subdomain</label>
                                    <input className="adm-text-input" type="text" value={editForm.subdomain} readOnly />
                                </div>
                                <div>
                                    <label className="adm-input-label">Account MID</label>
                                    <input className="adm-text-input" type="text" value={editForm.mid} readOnly />
                                </div>
                                <div>
                                    <label className="adm-input-label">Environment</label>
                                    <input className="adm-text-input" type="text" value={editForm.environment} readOnly />
                                </div>
                                <div>
                                    <label className="adm-input-label">Status</label>
                                    <select className="adm-text-input"
                                        value={editForm.status}
                                        onChange={e => updateEdit('status', e.target.value)}>
                                        <option value="Connected">Connected</option>
                                        <option value="Disconnected">Disconnected</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="adm-input-label">Client ID</label>
                                    <input className="adm-text-input" type="text"
                                        value={editForm.clientId} onChange={e => updateEdit('clientId', e.target.value)} />
                                </div>
                                <div>
                                    <label className="adm-input-label">Client Secret</label>
                                    <div className="adm-secret-field">
                                        <input className="adm-text-input" type="password" placeholder="Leave blank to keep current secret"
                                            value={editForm.clientSecret} onChange={e => updateEdit('clientSecret', e.target.value)} />
                                    </div>
                                    <p className="adm-input-hint mt-1">Only enter a value if you want to replace the existing client secret.</p>
                                </div>
                                {editError && (
                                    <p style={{ color: '#EF4444', margin: 0 }}>Failed to save: {editError}</p>
                                )}
                            </div>
                        </div>

                        <div className="adm-offcanvas-footer">
                            <button type="button" className="btn-admin btn-admin-secondary btn-admin-md" onClick={closeEdit} disabled={editSubmitting}>
                                Cancel
                            </button>
                            <button type="button" className="btn-admin btn-admin-primary btn-admin-md" onClick={submitEdit} disabled={editSubmitting || !editValid}>
                                {editSubmitting ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}

function ConnectionCard({ conn, expanded, onToggle, secretVisible, onToggleSecret, onEdit, onRemove, removing, onToggleStatus, toggling }) {
    return (
        <div className="adm-connection-card">
            <div className="adm-connection-header">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className={`adm-cloud-icon adm-cloud-${conn.envClass}`}>&#9729;</div>
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <h3 className="adm-conn-name">{conn.name}</h3>
                                <span className={`adm-badge adm-badge-${conn.statusClass}`}>
                                    <span className={`adm-badge-dot adm-badge-dot-${conn.statusClass}`} />
                                    {conn.status}
                                </span>
                                <span className={`adm-badge adm-badge-env-${conn.envClass}`}>{conn.env}</span>
                            </div>
                            <p className="adm-conn-meta">MID: {conn.mid} &middot; Last synced {conn.lastSynced}</p>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <button className="btn-admin btn-admin-ghost btn-admin-sm" type="button">&#8635; Sync Now</button>
                        <button className="btn-admin btn-admin-secondary btn-admin-sm" type="button" onClick={onEdit}>Edit</button>
                        <button className={`adm-expand-btn${expanded ? ' expanded' : ''}`} type="button" onClick={onToggle}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="adm-chevron-icon">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="adm-scope-wrap">
                    {conn.scopes.map(s => <span key={s} className="adm-scope-pill">{s}</span>)}
                </div>
            </div>

            {expanded && (
                <div className="adm-connection-body">
                    <p className="adm-credentials-title">API Credentials</p>
                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <div className="d-flex flex-column gap-2">
                                <label className="adm-input-label">Subdomain</label>
                                <input className="adm-text-input" type="text" value={conn.subdomain} readOnly />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="d-flex flex-column gap-2">
                                <label className="adm-input-label">MID (Account ID)</label>
                                <input className="adm-text-input" type="text" value={conn.mid} readOnly />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="d-flex flex-column gap-2">
                                <label className="adm-input-label">Client ID</label>
                                <input className="adm-text-input" type="text" value={conn.clientId} readOnly />
                            </div>
                        </div>
                        <div className="col-6">
                            <div className="d-flex flex-column gap-2">
                                <label className="adm-input-label">Client Secret</label>
                                <div className="adm-secret-field">
                                    <input className="adm-text-input" type={secretVisible ? 'text' : 'password'} value={conn.clientSecret} readOnly />
                                    <button type="button" className="adm-secret-toggle" onClick={onToggleSecret}>
                                        {secretVisible ? '👁' : '🙈'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row g-3 mb-2">
                        <div className="col-12">
                            <div className="d-flex flex-column gap-2">
                                <label className="adm-input-label">Auth URL</label>
                                <input className="adm-text-input" type="text" value={conn.authUrl} readOnly />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="d-flex flex-column gap-2">
                                <label className="adm-input-label">REST Base URL</label>
                                <input className="adm-text-input" type="text" value={conn.restUrl} readOnly />
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="d-flex flex-column gap-2">
                                <label className="adm-input-label">SOAP Base URL</label>
                                <input className="adm-text-input" type="text" value={conn.soapUrl} readOnly />
                            </div>
                        </div>
                    </div>

                    <p className="adm-credentials-title" style={{ marginTop: 16 }}>Users ({conn.users.length})</p>
                    {conn.users.length === 0 ? (
                        <p className="adm-input-hint" style={{ margin: 0 }}>No users assigned to this connection.</p>
                    ) : (
                        <div className="d-flex flex-column gap-2">
                            {conn.users.map(u => {
                                const status = u.status || 'Active';
                                const isActive = status.toLowerCase() === 'active';
                                const pillStyle = isActive
                                    ? { background: '#ECFDF5', color: '#059669', borderColor: '#A7F3D0' }
                                    : { background: '#FEF2F2', color: '#B91C1C', borderColor: '#FECACA' };
                                const dotColor = isActive ? '#10B981' : '#EF4444';
                                return (
                                    <div key={u.id || u.email} className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                                        style={{ padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff' }}>
                                        <div className="d-flex align-items-center gap-3">
                                            <div>
                                                <p style={{ margin: 0, fontWeight: 700, color: '#0F172A' }}>{u.name || '—'}</p>
                                                <p style={{ margin: 0, fontSize: 12, color: '#64748B' }}>{u.email}</p>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            {u.role && (
                                                <span className="adm-badge" style={{ background: '#F1F5F9', color: '#475569', borderColor: '#CBD5E1' }}>
                                                    {u.role}
                                                </span>
                                            )}
                                            <span className="adm-badge" style={{ ...pillStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                <span className="adm-badge-dot" style={{ background: dotColor }} />
                                                {status}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="d-flex justify-content-end gap-2" style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #E2E8F0' }}>
                        <button className="btn-admin btn-admin-secondary btn-admin-sm" type="button" onClick={onToggleStatus} disabled={toggling || removing}>
                            {toggling ? '…' : (conn.status === 'Connected' ? 'Deactivate' : 'Activate')}
                        </button>
                        <button className="btn-admin btn-admin-danger btn-admin-sm" type="button" onClick={onRemove} disabled={removing || toggling}>
                            {removing ? 'Removing…' : <>&#10005; Remove Connection</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MCConnections;
